import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ShareModal from '../components/Pet/ShareModal';
import LogoutConfirmModal from '../components/common/LogoutConfirmModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PetCard from '../components/Pet/PetCard';
import LocalizedLink from '../components/common/LocalizedLink';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import PetService from '../services/petService';
import { precomputePetSchedules } from '../utils/petCalculations';
import ListCard from '../components/Dashboard/ListCard';
import QuickPreviewModal from '../components/Dashboard/QuickPreviewModal';
import StatsCard from '../components/Dashboard/StatsCard';

const {
  FiPlus, FiGrid, FiList, FiSearch, FiAlertTriangle, FiShare2,
  FiLoader, FiRefreshCw, FiTrendingUp, FiHeart, FiStar,
  FiBarChart3, FiUsers, FiLogOut, FiX
} = FiIcons;

// ✅ 模組層級單一實例
const petService = new PetService();

const DashboardPage = () => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewPet, setPreviewPet] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [petToDelete, setPetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedPetId, setExpandedPetId] = useState(null);

  const { t, language } = useLanguage();
  const { signOut, isLoggingOut, user } = useAuth();
  const { navigateTo } = useLanguageNavigation();
  const location = useLocation();

  // ✅ 依賴 language（穩定字串）而非 t（函數物件）
  const computedPets = useMemo(() => {
    if (pets.length === 0) return [];
    return precomputePetSchedules(pets, t);
  }, [pets, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPets = useCallback(async (forceRefresh = false) => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await petService.getPets(forceRefresh);
      if (fetchError) throw fetchError;
      setPets(data || []);
    } catch (err) {
      console.error('獲取寵物資料失敗:', err);
      let msg = err.message || t('dashboard.fetchErrorTitle');
      if (msg === 'Failed to fetch' || msg.includes('NetworkError')) {
        msg = t('common.errors.failedToFetch');
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    if (user) fetchPets(true);
    else setLoading(false);
  }, [user, fetchPets]);

  useEffect(() => {
    if (!previewMode) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [previewMode]);

  useEffect(() => { setExpandedPetId(null); }, [viewMode]);

  const stats = useMemo(() => {
    if (!showStats || !computedPets.length) return null;
    const typeStats = {};
    let totalAgeInYears = 0;
    let petsWithAge = 0;
    let recentlyAdded = 0;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    computedPets.forEach(pet => {
      if (pet.type) typeStats[pet.type] = (typeStats[pet.type] || 0) + 1;
      if (pet._computed?.age) {
        const yearMatch = pet._computed.age.match(/(\d+)\s*(?:year|年)/);
        if (yearMatch) { totalAgeInYears += parseInt(yearMatch[1]); petsWithAge++; }
      }
      if (pet.created_at && new Date(pet.created_at) > weekAgo) recentlyAdded++;
    });

    const avgAge = petsWithAge > 0 ? (totalAgeInYears / petsWithAge).toFixed(1) : '0';
    const mostCommonType = Object.keys(typeStats).length > 0
      ? Object.entries(typeStats).sort(([, a], [, b]) => b - a)[0]
      : null;

    return { total: computedPets.length, types: typeStats, avgAge, recentlyAdded, mostCommonType };
  }, [computedPets, showStats]);

  const filteredPets = useMemo(() => {
    let filtered = computedPets;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(s) ||
        p.breed?.toLowerCase().includes(s) ||
        p.type?.toLowerCase().includes(s)
      );
    }
    if (filterBy !== 'all') filtered = filtered.filter(p => p.type === filterBy);
    if (sortBy === 'name')    return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (sortBy === 'created') return [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (sortBy === 'type')    return [...filtered].sort((a, b) => (a.type || '').localeCompare(b.type || ''));
    return filtered;
  }, [computedPets, searchTerm, filterBy, sortBy]);

  const petTypes = useMemo(() => (
    [...new Set(computedPets.map(p => p.type).filter(Boolean))]
  ), [computedPets]);

  const translatePetType = useCallback((type) => {
    if (!type) return t('common.unknown');
    const key = `common.species.${type}`;
    const result = t(key);
    return result !== key ? result : type;
  }, [t]);

  const handleDeleteClick = useCallback((pet) => {
    setPetToDelete(pet);
    setShowDeleteModal(true);
    setExpandedPetId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!petToDelete) return;
    setIsDeleting(true);
    try {
      const { success, error: deleteError } = await petService.deletePet(petToDelete.id);
      if (success) {
        setPets(prev => prev.filter(p => p.id !== petToDelete.id));
        setShowDeleteModal(false);
        setPetToDelete(null);
      } else throw deleteError;
    } catch (err) {
      console.error('刪除寵物失敗:', err);
      alert(err?.message || t('dashboard.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [petToDelete, t]);

  const handleCancelDelete = useCallback(() => {
    if (!isDeleting) { setShowDeleteModal(false); setPetToDelete(null); }
  }, [isDeleting]);

  const handleShare = useCallback((pet) => {
    setSelectedPet(pet);
    setIsShareModalOpen(true);
    setExpandedPetId(null);
  }, []);

  const handlePreview = useCallback((pet) => {
    setPreviewPet(pet);
    setPreviewMode(true);
    setExpandedPetId(null);
  }, []);

  const handleClosePreview  = useCallback(() => setPreviewMode(false), []);
  const handleCloseShare    = useCallback(() => setIsShareModalOpen(false), []);
  const handleLogoutClick   = useCallback(() => setShowLogoutModal(true), []);
  const handleCancelLogout  = useCallback(() => setShowLogoutModal(false), []);

  const handleNavigateToPet = useCallback((petId) => {
    setPreviewMode(false);
    navigateTo(`/pet/${petId}`);
  }, [navigateTo]);

  const handleRetry = useCallback(() => fetchPets(true), [fetchPets]);

  const handleConfirmLogout = useCallback(async () => {
    await signOut(location.pathname);
    setShowLogoutModal(false);
  }, [signOut, location.pathname]);

  const handleEditPet = useCallback((petId) => navigateTo(`/pet/edit/${petId}`), [navigateTo]);

  const handleToggleStats  = useCallback(() => setShowStats(v => !v), []);
  const handleClearFilters = useCallback(() => { setSearchTerm(''); setFilterBy('all'); }, []);
  const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);
  const handleFilterChange = useCallback((e) => setFilterBy(e.target.value), []);
  const handleSortChange   = useCallback((e) => setSortBy(e.target.value), []);
  const handleViewGrid     = useCallback(() => setViewMode('grid'), []);
  const handleViewList     = useCallback(() => setViewMode('list'), []);

  // ✅ renderContent 改為 useMemo，避免每次 render 重建函數
  const content = useMemo(() => {
    if (!user) return (
      <div className="text-center py-20">
        <p className="text-lg text-gray-600 mb-4">{t('dashboard.checkingAuth')}</p>
        <button
          onClick={() => navigateTo('/auth')}
          className="bg-blue-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
        >
          {t('auth.signInButton')}
        </button>
      </div>
    );

    if (loading) return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <SafeIcon icon={FiLoader} className="animate-spin text-4xl text-blue-500 mb-4" />
        <p className="text-lg text-gray-600">{t('dashboard.loadingPets')}</p>
      </div>
    );

    if (error) return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-8 rounded-xl text-center">
        <SafeIcon icon={FiAlertTriangle} className="text-5xl mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">{t('dashboard.fetchErrorTitle')}</h2>
        <p className="mb-4">{error}</p>
        <button onClick={handleRetry} className="bg-yellow-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-yellow-600 transition-colors">
          <SafeIcon icon={FiRefreshCw} className="inline mr-2" />{t('dashboard.retry')}
        </button>
      </div>
    );

    if (computedPets.length === 0) return (
      <div className="text-center py-20">
        <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-400 via-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-6xl mb-6">🐾</div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4">{t('dashboard.noPetsTitle')}</h2>
        <p className="text-gray-500 mb-8 text-lg">{t('dashboard.noPetsSubtitle')}</p>
        <LocalizedLink to="/pet/add" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105">
          <SafeIcon icon={FiPlus} className="mr-3 h-5 w-5" />{t('dashboard.addFirstPet')}
        </LocalizedLink>
      </div>
    );

    return (
      <>
        {showStats && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard icon={FiUsers}     title={t('dashboard.totalPets')}      value={stats.total}          subtitle={t('dashboard.registeredPets')} color="blue" />
            <StatsCard icon={FiTrendingUp} title={t('dashboard.averageAge')}    value={`${stats.avgAge} ${t('common.time.years')}`} subtitle={t('dashboard.allPets')} color="green" />
            <StatsCard icon={FiStar}      title={t('dashboard.weeklyAdded')}    value={stats.recentlyAdded}  subtitle={t('dashboard.recentlyAdded')}  color="purple" />
            <StatsCard icon={FiHeart}     title={t('dashboard.mostCommonType')} value={stats.mostCommonType ? translatePetType(stats.mostCommonType[0]) : 'N/A'} subtitle={stats.mostCommonType ? `${stats.mostCommonType[1]} ${t('common.species.pets')}` : ''} color="orange" />
          </div>
        )}

        <div className="mb-8 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder={t('dashboard.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <button
                onClick={handleToggleStats}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${showStats ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <SafeIcon icon={FiBarChart3} className="h-4 w-4" />
                <span className="hidden sm:inline">{t('dashboard.statistics')}</span>
              </button>
              <button
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${isLoggingOut ? 'bg-red-100 text-red-400 cursor-not-allowed' : 'bg-red-100 text-red-700 hover:bg-red-200 hover:scale-105'}`}
              >
                {isLoggingOut
                  ? <SafeIcon icon={FiLoader} className="h-4 w-4 animate-spin" />
                  : <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                }
                <span className="hidden sm:inline">{isLoggingOut ? t('navbar.loggingOut') : t('navbar.logout')}</span>
              </button>
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button onClick={handleViewGrid} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}>
                  <SafeIcon icon={FiGrid} className="h-4 w-4" />
                </button>
                <button onClick={handleViewList} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}>
                  <SafeIcon icon={FiList} className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <select value={filterBy} onChange={handleFilterChange} className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500">
              <option value="all">{t('common.species.all')}</option>
              {petTypes.map(type => (
                <option key={type} value={type} className="capitalize">{translatePetType(type)}</option>
              ))}
            </select>
            <select value={sortBy} onChange={handleSortChange} className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500">
              <option value="name">{t('dashboard.sortByName')}</option>
              <option value="created">{t('dashboard.sortByCreated')}</option>
              <option value="type">{t('dashboard.sortByType')}</option>
            </select>
            <div className="flex items-center text-sm text-gray-500">
              {t('dashboard.showingResults', { current: filteredPets.length, total: computedPets.length })}
            </div>
          </div>
        </div>

        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8' : 'space-y-6'}>
          {filteredPets.map(pet =>
            viewMode === 'grid' ? (
              <PetCard key={pet.id} pet={pet} onShare={handleShare} />
            ) : (
              <ListCard
                key={pet.id}
                pet={pet}
                expandedPetId={expandedPetId}
                setExpandedPetId={setExpandedPetId}
                onPreview={handlePreview}
                onShare={handleShare}
                onEdit={handleEditPet}
                onDelete={handleDeleteClick}
                t={t}
                translatePetType={translatePetType}
              />
            )
          )}
        </div>

        {filteredPets.length === 0 && (searchTerm || filterBy !== 'all') && (
          <div className="text-center py-12">
            <SafeIcon icon={FiSearch} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-500 mb-2">{t('dashboard.noMatchingPets')}</p>
            <p className="text-sm text-gray-400">
              {searchTerm && t('dashboard.searchKeyword', { keyword: searchTerm })}
              {searchTerm && filterBy !== 'all' && ' • '}
              {filterBy !== 'all' && t('dashboard.typeFilter', { type: translatePetType(filterBy) })}
            </p>
            <button onClick={handleClearFilters} className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
              {t('dashboard.clearFilters')}
            </button>
          </div>
        )}
      </>
    );
  }, [
    user, loading, error, computedPets, filteredPets, petTypes,
    showStats, stats, searchTerm, filterBy, sortBy, viewMode, expandedPetId,
    isLoggingOut, t, translatePetType,
    handleRetry, handleToggleStats, handleLogoutClick, handleSearchChange,
    handleFilterChange, handleSortChange, handleViewGrid, handleViewList,
    handleClearFilters, handleShare, handlePreview, handleEditPet, handleDeleteClick,
    setExpandedPetId, navigateTo,
  ]);

  return (
    <div className="container mx-auto px-4 py-8 pt-24 min-h-screen">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {t('dashboard.title')}
          </h1>
          <p className="text-gray-600 mt-2 text-lg">
            {t('dashboard.subtitle', { count: computedPets.length })}
          </p>
        </div>
        {user && computedPets.length > 0 && (
          <LocalizedLink to="/pet/add" className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105">
            <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
            <span className="hidden sm:inline">{t('dashboard.addPet')}</span>
            <span className="sm:hidden">{t('common.actions.add')}</span>
          </LocalizedLink>
        )}
      </div>

      {content}

      {/* ✅ ShareModal：selectedPet 存在才掛載，關閉時保留 selectedPet 確保動畫完成 */}
      {selectedPet && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={handleCloseShare}
          pet={selectedPet}
        />
      )}

      {/* ✅ QuickPreviewModal：previewMode=false 時完全不掛載 */}
      {previewMode && (
        <QuickPreviewModal
          previewPet={previewPet}
          onClose={handleClosePreview}
          onNavigate={handleNavigateToPet}
          translatePetType={translatePetType}
          t={t}
        />
      )}

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        isLoggingOut={isLoggingOut}
      />
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        petName={petToDelete?.name}
        isDeleting={isDeleting}
      />

      <AnimatePresence>
        {isLoggingOut && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-sm w-full mx-4">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('navbar.loggingOut')}</h2>
                <p className="text-gray-600 text-sm">{t('navbar.redirectingToLogin')}</p>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardPage;